import type { NotificationItem } from "../types/notifications.types";
import { NotificationType } from "../types/notifications.types";

/**
 * Map an entity-linked notification to an in-app route.
 * Pure function: no routing or auth side-effects. Returns null when the
 * notification has no navigable target.
 *
 * Supported linkage:
 *  - entityType = "education" / "enrollment" / "exam" / "survey" / "certificate" / "ticket"
 *  - OR specific NotificationType where the route is implicit.
 *
 * Falls back to `data.route` (server-supplied hint) when provided.
 */
export function resolveNotificationRoute(
  n: NotificationItem,
  options?: {
    /**
     * Yönetici panelindeki kullanıcı için destek talebi bildirimleri admin
     * sayfasına gitmeli — kursiyer sayfası staff menüsünde yok.
     */
    isStaff?: boolean;
  },
): string | null {
  const isStaff = options?.isStaff === true;
  const ticketRoute = isStaff
    ? "/dashboard/support-tickets"
    : "/dashboard/client-support-tickets";
  // Explicit server hint wins — but only when it targets a web dashboard route.
  // Backend artık `data.route` içinde MOBİL deep link path'leri gönderiyor
  // (ör. /my-educations/detail); web bunları takip etmemeli.
  const rawData = (n.data ?? {}) as Record<string, unknown>;
  if (typeof rawData.route === "string" && rawData.route.startsWith("/dashboard/")) {
    return rawData.route;
  }

  const type = Number(n.type);
  const id = n.entityId ?? "";

  // Type-driven routing (works even when entityType is missing).
  switch (type) {
    case NotificationType.CERTIFICATE_ISSUED:
      return "/dashboard/client-certificates";
    case NotificationType.EXAM_ELIGIBLE:
    case NotificationType.EXAM_COMPLETED:
      return "/dashboard/client-exams";
    case NotificationType.SURVEY_ELIGIBLE:
    case NotificationType.SURVEY_COMPLETED:
      return "/dashboard/client-polls";
    case NotificationType.EDUCATION_OPENED:
    case NotificationType.EDUCATION_UPCOMING_30D:
    case NotificationType.EDUCATION_UPCOMING_7D:
    case NotificationType.EDUCATION_UPCOMING_1D:
    case NotificationType.EDUCATION_UPDATED:
    case NotificationType.EDUCATION_DOCUMENT_ADDED:
    case NotificationType.VIRTUAL_CLASSROOM_COMPLETED:
    case NotificationType.MANDATORY_EDUCATION_ASSIGNED:
      return "/dashboard/client-educations";
    case NotificationType.ENROLLMENT_APPROVED:
      return "/dashboard/client-educations";
    case NotificationType.ENROLLMENT_REJECTED_FINAL:
      return "/dashboard/client-education-requests";
    case NotificationType.TICKET_REPLIED:
    case NotificationType.TICKET_CREATED:
      return ticketRoute;
    // Dinamik onay akışı bildirimleri: talebi doğrudan aç. Onaycının kaydı
    // listede bulabildiği tek yer eğitim ekranları ve orada satır statüye göre
    // gizlenebiliyor — bildirim, karar ekranına giden garantili yol.
    case NotificationType.APPROVAL_STEP_ASSIGNED:
    case NotificationType.APPROVAL_REQUEST_APPROVED:
    case NotificationType.APPROVAL_REQUEST_REJECTED: {
      const requestId =
        typeof rawData.approvalRequestID === "string" ? rawData.approvalRequestID : null;
      return requestId
        ? `/dashboard/request-and-approval?approvalRequestId=${encodeURIComponent(requestId)}`
        : "/dashboard/request-and-approval";
    }
    default:
      break;
  }

  // Entity-driven fallback.
  switch (n.entityType) {
    case "education":
      return id
        ? `/dashboard/client-educations?id=${encodeURIComponent(id)}`
        : "/dashboard/client-educations";
    case "enrollment":
      return "/dashboard/client-education-requests";
    case "exam":
      return "/dashboard/client-exams";
    case "survey":
      return "/dashboard/client-polls";
    case "certificate":
      return "/dashboard/client-certificates";
    case "ticket":
      return ticketRoute;
    default:
      return null;
  }
}
